import React, { useState, useEffect } from "react";
import { formatWhatsAppToMarkdown } from "../../lib/whatsappFormatter";
import { mapMessageTemplate } from "../../lib/messageMapper";
import { applyCalendarColumnMappingPreview } from "../../lib/calendarPreviewUtils";
import { MessageSquare, Lock, Type, FileText, Plus, Trash2, Phone, ExternalLink, Image, Reply } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import EmojiPicker, { EmojiStyle, Theme } from 'emoji-picker-react';
import es from 'emoji-picker-react/dist/data/emojis-es';
import { Twemoji } from 'react-emoji-render';
import { Card, CardContent, CardFooter } from "../ui/card";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { ColumnSelect } from "../ui/select";
import {
  checkVariableToWordRatio,
  validateMetaTemplateDraft,
} from "../../lib/metaTemplateUtils";
import { CALENDAR_STANDARD_VAR_NAMES } from "../../lib/calendarFieldOptions";
import { cleanGupshupBody } from "../../lib/templatePreview";

interface Props {
  messageTemplate: string;
  setMessageTemplate: (v: string) => void;
  sheetColumns: string[] | null;
  sampleRow?: Record<string, any> | null;
  phoneColumn?: string | null;
  templateId?: string | null;
  templates?: any[];
  googleSheetId?: string | null;
  columnMapping?: Record<string, string>;
  setColumnMapping?: (mapping: Record<string, string>) => void;
  reloadColumns?: () => Promise<void>;
  // ✅ Template components props (to persist between steps)
  headerText?: string;
  setHeaderText?: (v: string) => void;
  footerText?: string;
  setFooterText?: (v: string) => void;
  buttons?: Array<{ type: string; text: string; url?: string; phone_number?: string }>;
  setButtons?: (v: Array<{ type: string; text: string; url?: string; phone_number?: string }>) => void;
  // ✅ Header image props
  parentHeaderType?: 'TEXT' | 'IMAGE';
  setParentHeaderType?: (v: 'TEXT' | 'IMAGE') => void;
  parentHeaderImageHandle?: string;
  setParentHeaderImageHandle?: (v: string) => void;
  // ✅ New prop for GCS URL
  setHeaderImageUrl?: (v: string) => void;
  parentHeaderImagePreview?: string;
  setParentHeaderImagePreview?: (v: string) => void;
  // ✅ Toggle states for persistence
  parentHeaderEnabled?: boolean;
  setParentHeaderEnabled?: (v: boolean) => void;
  parentFooterEnabled?: boolean;
  setParentFooterEnabled?: (v: boolean) => void;
  parentButtonsEnabled?: boolean;
  setParentButtonsEnabled?: (v: boolean) => void;
  onBack: () => void;
  onNext: () => void;
  campaignType?: string;
  hideNavigation?: boolean;
}

const StepTemplateMessage: React.FC<Props> = ({
  messageTemplate, setMessageTemplate, sheetColumns, sampleRow, phoneColumn,
  templateId, templates, googleSheetId, columnMapping = {}, setColumnMapping,
  reloadColumns,
  // ✅ Props for template components (from parent hook)
  headerText: parentHeaderText,
  setHeaderText: parentSetHeaderText,
  footerText: parentFooterText,
  setFooterText: parentSetFooterText,
  buttons: parentButtons,
  setButtons: parentSetButtons,
  // ✅ Header image props (from parent hook)
  parentHeaderType,
  setParentHeaderType,
  parentHeaderImageHandle,
  setParentHeaderImageHandle,
  setHeaderImageUrl,
  parentHeaderImagePreview,
  setParentHeaderImagePreview,
  // ✅ Toggle states for persistence
  parentHeaderEnabled,
  setParentHeaderEnabled,
  parentFooterEnabled,
  setParentFooterEnabled,
  parentButtonsEnabled,
  setParentButtonsEnabled,
  onBack, onNext, campaignType,
  hideNavigation
}) => {
  const mappableVariables = Array.isArray(sheetColumns) ? sheetColumns : [];
  const variables = mappableVariables;
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [error, setError] = useState(false);
  const [unmappedVarsError, setUnmappedVarsError] = useState<string | null>(null);
  const [endsWithVarError, setEndsWithVarError] = useState<string | null>(null);

  // ===== ESTADOS PARA ELEMENTOS ADICIONALES (Header, Footer, Buttons) =====
  // Use parent state if provided, otherwise use local state
  const [localHeaderText, setLocalHeaderText] = useState("");
  const [localFooterText, setLocalFooterText] = useState("");
  const [localButtons, setLocalButtons] = useState<Array<{ type: 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER'; text: string; url?: string; phone_number?: string }>>([]);

  // Use parent values if available
  const headerText = parentHeaderText !== undefined ? parentHeaderText : localHeaderText;
  const setHeaderText = parentSetHeaderText || setLocalHeaderText;
  const footerText = parentFooterText !== undefined ? parentFooterText : localFooterText;
  const setFooterText = parentSetFooterText || setLocalFooterText;
  const buttons = (parentButtons !== undefined ? parentButtons : localButtons) as Array<{ type: 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER'; text: string; url?: string; phone_number?: string }>;
  const setButtons = (parentSetButtons || setLocalButtons) as (v: Array<{ type: 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER'; text: string; url?: string; phone_number?: string }>) => void;

  // Toggle states for showing/hiding sections - use parent if available
  const [localHeaderEnabled, setLocalHeaderEnabled] = useState(false);
  const [localFooterEnabled, setLocalFooterEnabled] = useState(false);
  const [localButtonsEnabled, setLocalButtonsEnabled] = useState(false);

  // Use parent values if available (for persistence across navigation)
  const headerEnabled = parentHeaderEnabled !== undefined ? parentHeaderEnabled : localHeaderEnabled;
  const setHeaderEnabled = setParentHeaderEnabled || setLocalHeaderEnabled;
  const footerEnabled = parentFooterEnabled !== undefined ? parentFooterEnabled : localFooterEnabled;
  const setFooterEnabled = setParentFooterEnabled || setLocalFooterEnabled;
  const buttonsEnabled = parentButtonsEnabled !== undefined ? parentButtonsEnabled : localButtonsEnabled;
  const setButtonsEnabled = setParentButtonsEnabled || setLocalButtonsEnabled;

  // ✅ Estados para header con imagen - use parent if available
  const [localHeaderType, setLocalHeaderType] = useState<'TEXT' | 'IMAGE'>('TEXT');
  const [localHeaderImageHandle, setLocalHeaderImageHandle] = useState('');
  const [localHeaderImagePreview, setLocalHeaderImagePreview] = useState('');

  // Use parent values if available, otherwise use local
  const headerType = parentHeaderType !== undefined ? parentHeaderType : localHeaderType;
  const setHeaderType = setParentHeaderType || setLocalHeaderType;
  const headerImageHandle = parentHeaderImageHandle !== undefined ? parentHeaderImageHandle : localHeaderImageHandle;
  const setHeaderImageHandle = setParentHeaderImageHandle || setLocalHeaderImageHandle;
  const headerImagePreview = parentHeaderImagePreview !== undefined ? parentHeaderImagePreview : localHeaderImagePreview;
  const setHeaderImagePreview = setParentHeaderImagePreview || setLocalHeaderImagePreview;

  // Check if user selected an existing template (read-only mode)
  const selectedTemplate = templateId && templates
    ? templates.find((t: any) => String(t.id ?? t._id) === String(templateId))
    : null;
  const isReadOnly = Boolean(selectedTemplate);

  const existingTemplateFooter =
    selectedTemplate?.footer_text ||
    selectedTemplate?.components?.find((c: any) => c.type === "FOOTER")?.text ||
    "";
  const rawBodyFromComponents =
    selectedTemplate?.components?.find((c: any) => c.type === "BODY")?.text || "";
  // Prefer cleaned template/body (Marketing/Calendar normalized); sanitize components.BODY fallback
  const rawExistingTemplateBody =
    selectedTemplate?.template ||
    selectedTemplate?.body ||
    rawBodyFromComponents ||
    "";

  const existingTemplateBody = rawExistingTemplateBody
    ? cleanGupshupBody(
        rawExistingTemplateBody,
        existingTemplateFooter
      )
    : "";
  const effectiveMessageTemplate = isReadOnly
    ? (existingTemplateBody || messageTemplate)
    : messageTemplate;

  // ✅ Extraer header, footer y buttons de la plantilla seleccionada
  const headerComp = selectedTemplate?.components?.find((c: any) => c.type === "HEADER");
  const existingTemplateHeader =
    selectedTemplate?.header_text ||
    (headerComp?.format === "TEXT" ? headerComp.text : "") ||
    "";
  const existingTemplateButtons = (() => {
    if (selectedTemplate?.buttons && selectedTemplate.buttons.length > 0) {
      return selectedTemplate.buttons;
    }
    const btnComp = selectedTemplate?.components?.find((c: any) => c.type === "BUTTONS");
    return (
      btnComp?.buttons?.map((b: any) => ({
        type: b.type,
        text: b.text,
        url: b.url,
        phone_number: b.phone_number,
      })) || []
    );
  })();

  // Extract template variables from the message ({{variableName}} or {{1}}, {{2}}, etc.)
  const extractTemplateVariables = (text: string): string[] => {
    const matches = text.match(/\{\{([^}]+)\}\}/g) || [];
    return matches.map(m => m.replace(/\{\{|\}\}/g, '').trim());
  };

  // Extract only numeric variables ({{1}}, {{2}}, etc.) for mapping
  const extractNumericVariables = (text: string): string[] => {
    const allVars = extractTemplateVariables(text);
    return allVars.filter(v => /^\d+$/.test(v));
  };

  // ✅ Get numeric variables from ALL parts of the template (body, header, buttons)
  // Mapping UI only for existing Meta templates ({{1}}, {{2}}). New drafts auto-map at submit.
  const bodyNumericVariables = extractNumericVariables(effectiveMessageTemplate);
  const headerNumericVariables = isReadOnly ? extractNumericVariables(existingTemplateHeader) : [];
  const buttonNumericVariables = isReadOnly
    ? existingTemplateButtons.flatMap((btn: any) => [
      ...extractNumericVariables(btn.text || ''),
      ...extractNumericVariables(btn.url || '')
    ])
    : [];

  // ✅ Combine all unique numeric variables
  const allNumericVariables = Array.from(new Set([
    ...bodyNumericVariables,
    ...headerNumericVariables,
    ...buttonNumericVariables
  ])).sort((a, b) => parseInt(a) - parseInt(b));

  // Legacy: keep numericVariables for backward compatibility
  const numericVariables = allNumericVariables;
  const hasNumericVariables = allNumericVariables.length > 0 && isReadOnly;

  const buildWhatsAppPreviewText = (text: string, numericVars?: string[]): string => {
    if (!text) return "";
    let previewText = text;
    if (sampleRow && columnMapping && Object.keys(columnMapping).length > 0) {
      if (campaignType === "Calendar") {
        previewText = applyCalendarColumnMappingPreview(previewText, columnMapping, sampleRow);
      } else {
        const vars = numericVars ?? extractNumericVariables(text);
        vars.forEach((varNum) => {
          const columnName = columnMapping[varNum];
          if (columnName && sampleRow[columnName] !== undefined) {
            const regex = new RegExp(`\\{\\{${varNum}\\}\\}`, "g");
            previewText = previewText.replace(regex, String(sampleRow[columnName]));
          }
        });
      }
    }
    // Calendar: only replace variables explicitly mapped in columnMapping — never auto-fill from sampleRow
    if (sampleRow && campaignType !== "Calendar") {
      previewText = mapMessageTemplate(previewText, sampleRow);
    }
    return previewText
      .replace(/\*([^*]+)\*/g, "$1")
      .replace(/_([^_]+)_/g, "$1")
      .replace(/~([^~]+)~/g, "$1");
  };

  // ✅ Check if ALL numeric variables (from body, header, buttons) are mapped
  const allNumericVarsMapped = allNumericVariables.every(v => columnMapping[v] && columnMapping[v].trim() !== '');

  // State for mapping error
  const [mappingError, setMappingError] = useState<string | null>(null);

  const [validationError, setValidationError] = useState<string | null>(null);

  const countEmojis = (text: string): number => {
    const emojiRegex = /(\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/g;
    const matches = text.match(emojiRegex);
    return matches ? matches.length : 0;
  };

  const isValidPhoneNumber = (phone: string): boolean => {
    // Meta requires: Start with +, digits only, 10-16 digits total (including country code)
    // Regex: ^\+\d{10,15}$  (Note: allows 10-15 digits after +, total length 11-16)
    return /^\+\d{10,15}$/.test(phone);
  };

  // Meta template validation rules:
  // 1. Cannot START with a variable
  // 2. Cannot END with a variable
  // 3. Cannot END with variable + only punctuation (like {{Variable}}.)
  // Variables in the middle are perfectly fine

  const checkStartsWithVariable = (text: string): boolean => {
    const trimmed = text.trim();
    // Matches if starts with {{...}}
    return /^\{\{[^}]+\}\}/.test(trimmed);
  };

  const checkEndsWithVariable = (text: string): boolean => {
    const trimmed = text.trim();
    // Matches if ends with {{...}} optionally followed by punctuation only
    // Esto detecta: "...{{Variable}}" o "...{{Variable}}." o "...{{Variable}}!"
    return /\{\{[^}]+\}\}[.!?,;:]*$/.test(trimmed);
  };

  // Validar proporción de variables vs palabras (regla de Meta)
  const checkVariableToWordRatio = (text: string): { valid: boolean; ratio: number; variables: number; words: number } => {
    // Contar variables {{...}}
    const variableMatches = text.match(/\{\{[^}]+\}\}/g) || [];
    const variableCount = variableMatches.length;

    // Remover variables del texto para contar palabras reales
    const textWithoutVariables = text.replace(/\{\{[^}]+\}\}/g, '').trim();

    // Contar palabras (mínimo 1 caracter, ignorar espacios múltiples)
    const words = textWithoutVariables.split(/\s+/).filter(word => word.length > 0);
    const wordCount = words.length;

    // Meta requiere aproximadamente 3-4 palabras por variable como mínimo
    // Si tienes 3 variables, necesitas al menos ~12 palabras de texto real
    const minWordsPerVariable = 4;
    const requiredWords = variableCount * minWordsPerVariable;

    const ratio = variableCount > 0 ? wordCount / variableCount : Infinity;
    const valid = variableCount === 0 || wordCount >= requiredWords;

    return { valid, ratio, variables: variableCount, words: wordCount };
  };

  // When an existing template is selected, load its content into the editor
  useEffect(() => {
    if (!selectedTemplate || !existingTemplateBody) return;

    if (existingTemplateBody !== messageTemplate) {
      setMessageTemplate(existingTemplateBody);
    }
  }, [
    selectedTemplate,
    existingTemplateBody,
    messageTemplate,
    setMessageTemplate
  ]);

  // Clear errors when messageTemplate changes
  useEffect(() => {
    if (unmappedVarsError) {
      setUnmappedVarsError(null);
    }
    if (endsWithVarError) {
      setEndsWithVarError(null);
    }
    if (validationError) {
      setValidationError(null);
    }
  }, [messageTemplate]);

  // ✅ Clear mapping error when user changes column mapping
  useEffect(() => {
    if (mappingError) {
      setMappingError(null);
    }
  }, [columnMapping]);

  // ✅ Intelligent Tour logic for Step 4 (variable mapping for EXISTING templates)
  useEffect(() => {
    // Only fire this tour when user selected an existing approved template (isReadOnly)
    // When creating a new template, the startMarketingCreateTemplateMessageTour handles it.
    if (hasNumericVariables && isReadOnly) {
      const isRenewal = campaignType === 'Renovación' || campaignType === 'Renovación de servicio' || window.location.pathname.toLowerCase().includes('renovacion') || window.location.pathname.toLowerCase().includes('renewal');
      const tourKey = isRenewal ? 'renewal_tour_step4_seen' : 'marketing_tour_step4_seen';

      const triggerStage4 = () => {
        return setTimeout(() => {
          if (isRenewal) {
            import('@/components/common/RenewalCampaignTour').then(({ startRenewalStep4Tour }) => {
              startRenewalStep4Tour(() => localStorage.setItem(tourKey, 'true'));
            }).catch(e => console.error('Error cargando Renewal Step4 Tour:', e));
          } else if (campaignType === 'Dynamic') {
            import('@/components/common/DynamicCampaignTour').then(({ startDynamicStep4Tour }) => {
              startDynamicStep4Tour(() => localStorage.setItem(tourKey, 'true'));
            }).catch(e => console.error('Error cargando Dynamic Step4 Tour:', e));
          } else {
            import('@/components/common/MarketingCampaignTour').then(({ startMarketingStep4Tour }) => {
              startMarketingStep4Tour(() => localStorage.setItem(tourKey, 'true'));
            }).catch(e => console.error('Error cargando Marketing Step4 Tour:', e));
          }
        }, 300);
      };

      let timeoutId: NodeJS.Timeout | undefined;

      const hasSeen = localStorage.getItem(tourKey);
      if (!hasSeen) {
        timeoutId = triggerStage4();
      }

      const handleRestart = (e: Event) => {
        if (e instanceof CustomEvent && e.detail) e.detail.handled = true;
        localStorage.removeItem(tourKey);
        timeoutId = triggerStage4();
      };

      window.addEventListener('restart-local-tour', handleRestart);

      return () => {
        if (timeoutId) clearTimeout(timeoutId);
        window.removeEventListener('restart-local-tour', handleRestart);
      };
    }
  }, [hasNumericVariables, isReadOnly, campaignType]);

  // ✅ Intelligent Tour logic for Create Template Message (Step 2 inside Create Template flow)
  useEffect(() => {
    if (!isReadOnly) {
      const isRenewal = campaignType === 'Renovación' || campaignType === 'Renovación de servicio' || 
                        window.location.pathname.toLowerCase().includes('renovacion') || 
                        window.location.pathname.toLowerCase().includes('renewal');
      const tourKey = isRenewal ? 'renewal_tour_create_template_msg_seen' : 'marketing_tour_create_template_msg_seen';

      // Always clear keys on mount in create-template mode to force a fresh tour
      sessionStorage.removeItem(tourKey);
      localStorage.removeItem(tourKey);

      let timeoutId: NodeJS.Timeout | undefined;
      let retryCount = 0;
      const MAX_RETRIES = 3;

      const triggerCreateMsgTour = () => {
        timeoutId = setTimeout(() => {
          // Check if the target element exists before starting
          // We look for the main ID we need for the first step
          const targetEl = document.querySelector('#tour-marketing-template-variables');
          
          if (!targetEl && retryCount < MAX_RETRIES) {
            retryCount++;
            triggerCreateMsgTour();
            return;
          }

          if (isRenewal) {
            import('@/components/common/RenewalCampaignTour').then(({ startRenewalCreateTemplateMessageTour }) => {
              if (startRenewalCreateTemplateMessageTour) {
                startRenewalCreateTemplateMessageTour(() => sessionStorage.setItem(tourKey, 'true'));
              }
            }).catch(e => console.error('Error cargando Renewal Create Template Message Tour:', e));
          } else if (campaignType === 'Dynamic') {
            import('@/components/common/DynamicCampaignTour').then(({ startDynamicCreateTemplateMessageTour }) => {
              if (startDynamicCreateTemplateMessageTour) {
                startDynamicCreateTemplateMessageTour(() => sessionStorage.setItem(tourKey, 'true'));
              }
            }).catch(e => console.error('Error cargando Dynamic Create Template Message Tour:', e));
          } else {
            import('@/components/common/MarketingCampaignTour').then(({ startMarketingCreateTemplateMessageTour }) => {
              if (startMarketingCreateTemplateMessageTour) {
                startMarketingCreateTemplateMessageTour(() => sessionStorage.setItem(tourKey, 'true'));
              }
            }).catch(e => console.error('Error cargando Marketing Create Template Message Tour:', e));
          }
        }, retryCount === 0 ? 1000 : 500); // 1s initial delay, then 0.5s retries
      };

      triggerCreateMsgTour();

      const handleRestart = (e: Event) => {
        if (e instanceof CustomEvent && e.detail && !e.detail.handled) {
          e.detail.handled = true;
          sessionStorage.removeItem(tourKey);
          localStorage.removeItem(tourKey);
          retryCount = 0;
          triggerCreateMsgTour();
        }
      };

      window.addEventListener('restart-local-tour', handleRestart);

      return () => {
        if (timeoutId) clearTimeout(timeoutId);
        window.removeEventListener('restart-local-tour', handleRestart);
      };
    }
  }, [isReadOnly, campaignType]);


  const handleNext = () => {
    // Clear previous errors
    setError(false);
    setUnmappedVarsError(null);
    setEndsWithVarError(null);
    setMappingError(null);
    setValidationError(null);

    // Validate message is not empty
    if (!messageTemplate || messageTemplate.trim().length === 0) {
      setError(true);
      return;
    }

    // Validate that all numeric variables are mapped (for existing Meta templates)
    if (hasNumericVariables && !allNumericVarsMapped) {
      const unmapped = allNumericVariables.filter(v => !columnMapping[v] || columnMapping[v].trim() === '');

      // Determinar de qué sección son las variables sin mapear
      const unmappedBody = unmapped.filter(v => bodyNumericVariables.includes(v));
      const unmappedHeader = unmapped.filter(v => headerNumericVariables.includes(v));
      const unmappedButtons = unmapped.filter(v => buttonNumericVariables.includes(v));

      let errorParts: string[] = [];
      if (unmappedBody.length > 0) {
        errorParts.push(`Cuerpo: ${unmappedBody.map(v => `{{${v}}}`).join(', ')}`);
      }
      if (unmappedHeader.length > 0) {
        errorParts.push(`Encabezado: ${unmappedHeader.map(v => `{{${v}}}`).join(', ')}`);
      }
      if (unmappedButtons.length > 0) {
        errorParts.push(`Botones: ${unmappedButtons.map(v => `{{${v}}}`).join(', ')}`);
      }

      setMappingError(`Debes conectar todos los datos antes de continuar. Faltan: ${errorParts.join(' | ')}`);
      return;
    }

    // Validar plantilla nueva (cuerpo, encabezado, botones) antes de avanzar
    if (!isReadOnly) {
      const draftValidation = validateMetaTemplateDraft({
        bodyText: messageTemplate,
        headerEnabled,
        headerType,
        headerText,
        headerImageHandle,
        footerEnabled,
        footerText,
        buttonsEnabled,
        buttons,
      });

      if (!draftValidation.valid) {
        const msg = draftValidation.message;
        if (msg.includes('encabezado') || msg.includes('header') || msg.includes('imagen')) {
          setEndsWithVarError(msg);
        } else {
          setValidationError(msg);
        }
        return;
      }
    }

    // Extract variables from the template
    const templateVars = extractTemplateVariables(messageTemplate);

    // If template has variables, validate they exist in sheet columns / calendar event fields
    const validationColumns = campaignType === "Calendar" ? mappableVariables : variables;
    if (templateVars.length > 0 && validationColumns.length > 0) {
      const normalizedCols = validationColumns.map((v) => v.toLowerCase().trim());

      const unmappedVars = templateVars.filter((v) => {
        if (/^\d+$/.test(v)) return false;
        const lower = v.toLowerCase().trim();
        if (campaignType === "Calendar" && CALENDAR_STANDARD_VAR_NAMES.has(lower)) return false;
        return !normalizedCols.includes(lower);
      });

      if (unmappedVars.length > 0) {
        setUnmappedVarsError(
          campaignType === "Calendar"
            ? `Las siguientes variables no se encontraron en los datos del calendario: ${unmappedVars.map((v) => `{{${v}}}`).join(', ')}. Usa las variables del evento disponibles o los campos estándar (Fecha, Hora, Ubicación).`
            : `Las siguientes variables no se encontraron en la hoja de cálculo: ${unmappedVars.map((v) => `{{${v}}}`).join(', ')}. Verifica que los nombres coincidan exactamente con las columnas de tu hoja.`
        );
        return;
      }
    }

    onNext();
  };

  const insertVariable = (variableName: string) => {
    const ta = document.getElementById('messageEditor') as HTMLTextAreaElement | null;
    const token = `{{${variableName.trim()}}}`;
    if (!ta) {
      setMessageTemplate(messageTemplate + token);
      return;
    }
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? 0;
    const before = messageTemplate.substring(0, start);
    const after = messageTemplate.substring(end);
    const newText = before + token + after;
    setMessageTemplate(newText);
    // restore cursor position after insertion
    setTimeout(() => {
      ta.focus();
      const pos = start + token.length;
      ta.setSelectionRange(pos, pos);
    }, 0);
  };

  const insertEmoji = (emojiData: any) => {
    const ta = document.getElementById('messageEditor') as HTMLTextAreaElement | null;
    const emoji = emojiData?.emoji ?? emojiData?.unified ?? '';
    if (!ta) {
      setMessageTemplate(messageTemplate + emoji);
      setShowEmojiPicker(false);
      return;
    }
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? 0;
    const before = messageTemplate.substring(0, start);
    const after = messageTemplate.substring(end);
    const newText = before + emoji + after;
    setMessageTemplate(newText);
    setShowEmojiPicker(false);
    // restore cursor position after insertion
    setTimeout(() => {
      ta.focus();
      const pos = start + emoji.length;
      ta.setSelectionRange(pos, pos);
    }, 0);
  };

  return (
    <Card className="mt-6 relative">
      <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl bg-gradient-to-b from-[#1CB3B3] to-[#179999]" />
      <CardContent className="space-y-6 p-6 pl-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex-none bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] text-white flex items-center justify-center">
            <MessageSquare className="w-5 h-5" />
          </div>
          <div>
            <div className="text-lg font-semibold text-gray-900">Crear Mensaje</div>
            <p className="text-xs text-gray-400">
              {campaignType === "Calendar" 
                ? "Crea el mensaje utilizando los datos de tu calendario; el editor se mostrará aquí."
                : "Crea el mensaje utilizando los datos de la hoja de cálculo; el editor se mostrará aquí."}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            {/* Read-only banner when template is selected */}
            {isReadOnly && (
              <div className="mb-4 p-4 rounded-xl bg-blue-50 border border-blue-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Lock className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-blue-900">
                      Plantilla seleccionada: {selectedTemplate?.displayName
                        ?? selectedTemplate?.name?.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
                        ?? 'Sin nombre'}
                    </div>
                    <p className="text-sm text-blue-700 mt-0.5">
                      El contenido de esta plantilla fue aprobado por Meta y no puede editarse.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Variable Mapping Section - shown when template has numeric variables */}
            {hasNumericVariables && (
              <div id="tour-marketing-mapping-container" className="mb-4 p-4 rounded-xl bg-[#E8F7F7] border border-[#1CB3B3]/30">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-lg">🔗</span>
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">
                      {campaignType === "Calendar" ? "Conectar datos de tu calendario" : "Conectar datos de tu hoja"}
                    </div>
                    <p className="text-sm text-gray-600 mt-0.5">
                      {campaignType === "Calendar" 
                        ? "Esta plantilla necesita información de tu calendario. Selecciona qué dato del evento se usará en cada parte del mensaje."
                        : "Esta plantilla necesita información de tu hoja de cálculo. Selecciona qué columna se usará en cada parte del mensaje."}
                      {(headerNumericVariables.length > 0 || buttonNumericVariables.length > 0) && (
                        <span className="block mt-1 text-xs text-[#0D7377] font-medium">
                          Datos detectados en: {bodyNumericVariables.length > 0 && 'Cuerpo'}
                          {headerNumericVariables.length > 0 && (bodyNumericVariables.length > 0 ? ', ' : '') + 'Encabezado'}
                          {buttonNumericVariables.length > 0 && ((bodyNumericVariables.length > 0 || headerNumericVariables.length > 0) ? ', ' : '') + 'Botones'}
                        </span>
                      )}
                    </p>
                    {/* ✅ Botones en la misma fila después del texto */}
                    <div id="tour-marketing-mapping-actions" className="mt-3 flex items-center gap-2">
                      {googleSheetId && (
                        <button
                          id="tour-marketing-open-sheet"
                          type="button"
                          onClick={() => {
                            const url = `https://docs.google.com/spreadsheets/d/${googleSheetId}`;
                            window.open(url, "_blank", "noopener,noreferrer");
                          }}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-[#1CB3B3] text-[#0D7377] text-sm font-medium hover:bg-[#E8F7F7] transition-all"
                        >
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" />
                          </svg>
                          Abrir hoja
                        </button>
                      )}
                      {reloadColumns && (
                        <button
                          id="tour-marketing-update-columns"
                          type="button"
                          onClick={async (e) => {
                            const btn = e.currentTarget;
                            btn.disabled = true;
                            btn.classList.add('opacity-70');
                            // Añadir animación de spin al icono
                            const icon = btn.querySelector('svg');
                            if (icon) icon.classList.add('animate-spin');

                            await reloadColumns();
                            // Limpiar mapeo para forzar reasignación
                            if (setColumnMapping) {
                              setColumnMapping({});
                            }

                            // Restaurar botón
                            btn.disabled = false;
                            btn.classList.remove('opacity-70');
                            if (icon) icon.classList.remove('animate-spin');
                          }}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-[#1CB3B3] to-[#179999] text-white text-sm font-medium hover:shadow-lg transition-all"
                        >
                          <svg className="w-4 h-4 transition-transform" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
                          </svg>
                          {campaignType === "Calendar" ? "Actualizar eventos" : "Actualizar columnas"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {bodyNumericVariables.length > 0 && (
                  <div className="space-y-3">
                    <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    </div>
                    {bodyNumericVariables.map((varNum) => {
                      const usedColumns = Object.entries(columnMapping)
                        .filter(([key, value]) => key !== varNum && value)
                        .map(([_, value]) => value);
                      const availableColumns = variables.filter(col => !usedColumns.includes(col));
                      return (
                        <div key={`body-${varNum}`} className="flex items-center gap-3">
                          <div className="w-20 flex-shrink-0">
                            <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-[#1CB3B3]/20 text-[#0D7377] font-mono text-sm font-semibold">
                              {`{{${varNum}}}`}
                            </span>
                          </div>
                          <span className="text-gray-500">→</span>
                          <div className="flex-1">
                            <ColumnSelect
                              label=""
                              placeholder={campaignType === "Calendar" ? "Seleccionar dato del evento..." : "Seleccionar columna..."}
                              options={mappableVariables.filter(col => !usedColumns.includes(col))}
                              value={columnMapping[varNum] || null}
                              onChange={(val) => {
                                if (setColumnMapping) {
                                  setColumnMapping({ ...columnMapping, [varNum]: val || '' });
                                }
                              }}
                            />
                          </div>
                          {columnMapping[varNum] && <span className="text-[#1CB3B3] text-lg">✓</span>}
                        </div>
                      );
                    })}
                  </div>
                )}

                {headerNumericVariables.length > 0 && (
                  <div className="space-y-3 mt-4 pt-4 border-t border-[#1CB3B3]/20">
                    <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      DATOS DEL ENCABEZADO
                    </div>
                    {headerNumericVariables.map((varNum) => {
                      const usedColumns = Object.entries(columnMapping)
                        .filter(([key, value]) => key !== varNum && value)
                        .map(([_, value]) => value);
                      const availableColumns = variables.filter(col => !usedColumns.includes(col));
                      return (
                        <div key={`header-${varNum}`} className="flex items-center gap-3">
                          <div className="w-20 flex-shrink-0">
                            <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-[#1CB3B3]/20 text-[#0D7377] font-mono text-sm font-semibold">
                              {`{{${varNum}}}`}
                            </span>
                          </div>
                          <span className="text-gray-500">→</span>
                          <div className="flex-1">
                            <ColumnSelect
                              label=""
                              placeholder={campaignType === "Calendar" ? "Seleccionar dato del evento..." : "Seleccionar columna..."}
                              options={mappableVariables.filter(col => !usedColumns.includes(col))}
                              value={columnMapping[varNum] || null}
                              onChange={(val) => {
                                if (setColumnMapping) {
                                  setColumnMapping({ ...columnMapping, [varNum]: val || '' });
                                }
                              }}
                            />
                          </div>
                          {columnMapping[varNum] && <span className="text-[#1CB3B3] text-lg">✓</span>}
                        </div>
                      );
                    })}
                  </div>
                )}

                {buttonNumericVariables.length > 0 && (
                  <div className="space-y-3 mt-4 pt-4 border-t border-[#1CB3B3]/20">
                    <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      DATOS DE BOTONES
                    </div>
                    {buttonNumericVariables.map((varNum) => {
                      const usedColumns = Object.entries(columnMapping)
                        .filter(([key, value]) => key !== varNum && value)
                        .map(([_, value]) => value);
                      const columnsForMapping = mappableVariables.filter(col => !usedColumns.includes(col));
                      return (
                        <div key={`button-${varNum}`} className="flex items-center gap-3">
                          <div className="w-20 flex-shrink-0">
                            <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-[#1CB3B3]/20 text-[#0D7377] font-mono text-sm font-semibold">
                              {`{{${varNum}}}`}
                            </span>
                          </div>
                          <span className="text-gray-500">→</span>
                          <div className="flex-1">
                            <ColumnSelect
                              label=""
                              placeholder={campaignType === "Calendar" ? "Seleccionar dato del evento..." : "Seleccionar columna..."}
                              options={columnsForMapping}
                              value={columnMapping[varNum] || null}
                              onChange={(val) => {
                                if (setColumnMapping) {
                                  setColumnMapping({ ...columnMapping, [varNum]: val || '' });
                                }
                              }}
                            />
                          </div>
                          {columnMapping[varNum] && <span className="text-[#1CB3B3] text-lg">✓</span>}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Info note about header row */}
                <p className="mt-3 text-xs text-gray-500 flex items-center gap-1.5">
                  <span className="w-4 h-4 rounded-full bg-[#1CB3B3]/20 text-[#0D7377] flex items-center justify-center text-[10px] font-bold flex-shrink-0">i</span>
                  {campaignType === "Calendar" 
                    ? "Los datos se extraen de la descripción del evento siguiendo el formato 'Nombre: Valor'."
                    : "Recuerda que la primera fila de tu hoja es la cabecera con los nombres de las columnas."}
                </p>

                {mappingError && (
                  <div className="mt-3 p-3 bg-rose-50 border border-rose-200 rounded-lg">
                    <p className="text-sm text-rose-700 font-medium">⚠️ {mappingError}</p>
                  </div>
                )}

                {allNumericVarsMapped && (
                  <>
                    <div className="mt-3 p-3 bg-[#E8F7F7] border border-[#1CB3B3] rounded-lg">
                      <p className="text-sm text-[#0D7377] font-medium flex items-center gap-2">
                        <span className="text-[#1CB3B3]">✓</span>
                        Todos los datos están conectados correctamente
                      </p>
                    </div>
                    {/* ⚠️ Advertencia sobre datos incompletos */}
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <span className="text-amber-500 flex-shrink-0">⚠️</span>
                        <div>
                          <p className="text-sm text-amber-800 font-medium">
                            {campaignType === "Calendar" ? "Verifica los datos de tu calendario" : "Verifica los datos de tu hoja"}
                          </p>
                          <p className="text-xs text-amber-700 mt-1">
                            {campaignType === "Calendar"
                              ? "Asegúrate de que tus eventos tengan la información requerida en la descripción."
                              : "Asegúrate de que todas las filas tengan datos en las columnas seleccionadas."}
                            Las filas con datos faltantes se enviarán con campos vacíos.
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Variables section - hidden in read-only mode */}
            {!isReadOnly && (
              <div id="tour-marketing-template-variables" className="bg-slate-50 border border-slate-100 rounded-xl p-4 relative overflow-hidden pl-5 mb-4">
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#1CB3B3] to-[#179999]" />
                <div className="flex items-center justify-between mb-3">
                  <span className="text-lg font-semibold text-gray-900">
                    {campaignType === "Calendar" ? "Insertar variable del evento" : "Insertar dato de la hoja"}
                  </span>
                  <span className="text-xs text-slate-400">
                    {variables.length} {campaignType === "Calendar" ? "variable(s)" : "columna(s)"} disponible(s)
                  </span>
                </div>

                {/* Botones de acción */}
                <div id="tour-marketing-sheet-actions" className="flex flex-col sm:flex-row gap-2 mb-4">
                  {(googleSheetId || campaignType === "Calendar") && (
                    <button
                      id="tour-marketing-open-sheet-new"
                      type="button"
                      onClick={() => {
                        const url = campaignType === "Calendar" 
                          ? "https://calendar.google.com" 
                          : `https://docs.google.com/spreadsheets/d/${googleSheetId}`;
                        window.open(url, "_blank", "noopener,noreferrer");
                      }}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white border border-[#1CB3B3] text-[#0D7377] text-sm font-medium hover:bg-[#E8F7F7] transition-all"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                        {campaignType === "Calendar" ? (
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        ) : (
                          <path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" />
                        )}
                      </svg>
                      {campaignType === "Calendar" ? "Abrir calendario" : "Abrir hoja"}
                    </button>
                  )}
                  {reloadColumns && (
                    <button
                      id="tour-marketing-update-columns-new"
                      type="button"
                      onClick={async (e) => {
                        const btn = e.currentTarget;
                        btn.disabled = true;
                        btn.classList.add('opacity-70');
                        const icon = btn.querySelector('svg');
                        if (icon) icon.classList.add('animate-spin');
                        await reloadColumns();
                        btn.disabled = false;
                        btn.classList.remove('opacity-70');
                        if (icon) icon.classList.remove('animate-spin');
                      }}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-[#1CB3B3] to-[#179999] text-white text-sm font-medium hover:shadow-lg transition-all"
                    >
                      <svg className="w-4 h-4 transition-transform" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
                      </svg>
                      {campaignType === "Calendar" ? "Actualizar eventos" : "Actualizar columnas"}
                    </button>
                  )}
                </div>

                {/* Texto descriptivo breve */}
                <p className="text-xs text-slate-500 leading-relaxed mt-2">
                  {campaignType === "Calendar" 
                    ? "Selecciona una variable para crear tu mensaje. Los datos reales se obtendrán directamente de cada evento."
                    : "Abre tu hoja y añade la columna para crear tu mensaje. Asegúrate de que los datos correspondan."}
                </p>
              </div>
            )}

            <div className="mb-3">
              <Label className="text-lg font-semibold text-gray-900">
                {isReadOnly ? "Contenido de la plantilla" : "Editor de mensaje"}
              </Label>
            </div>

            {/* Para plantillas existentes: mostrar header, body, footer y buttons */}
            {isReadOnly ? (
              <div className="w-full rounded-md border border-gray-200 bg-gray-50 p-4 space-y-3">
                {/* Header */}
                {(selectedTemplate?.header_type === 'IMAGE' || existingTemplateHeader) && (
                  <div>
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Encabezado</span>

                    {/* Mostrar imagen si es tipo IMAGE */}
                    {selectedTemplate?.header_type === 'IMAGE' && selectedTemplate?.header_image_url && (
                      <div className="mt-2 mb-2">
                        <img
                          src={selectedTemplate.header_image_url}
                          alt="Encabezado"
                          className="w-full h-auto max-h-[200px] object-cover rounded-lg border border-gray-200"
                        />
                      </div>
                    )}

                    {/* Mostrar texto si existe (para tipo TEXTO) */}
                    {existingTemplateHeader && (
                      <p className="text-sm font-semibold text-gray-800 mt-1">{existingTemplateHeader}</p>
                    )}
                  </div>
                )}

                {/* Body */}
                <div>
                  {(selectedTemplate?.header_type === 'IMAGE' || existingTemplateHeader) && <div className="border-t border-gray-200 pt-3"></div>}
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Cuerpo</span>
                  <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{effectiveMessageTemplate}</p>
                </div>

                {/* Footer */}
                {existingTemplateFooter && (
                  <div className="border-t border-gray-200 pt-3">
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Pie de página</span>
                    <p className="text-xs text-gray-500 mt-1">{existingTemplateFooter}</p>
                  </div>
                )}

                {/* Buttons */}
                {existingTemplateButtons.length > 0 && (
                  <div className="border-t border-gray-200 pt-3">
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Botones</span>
                    <div className="mt-2 space-y-1">
                      {existingTemplateButtons.map((btn: any, idx: number) => (
                        <div key={idx} className="flex items-center gap-2 text-sm text-[#1CB3B3]">
                          {btn.type === 'URL' && <ExternalLink className="w-3.5 h-3.5" />}
                          {btn.type === 'PHONE_NUMBER' && <Phone className="w-3.5 h-3.5" />}
                          {btn.type === 'QUICK_REPLY' && <MessageSquare className="w-3.5 h-3.5" />}
                          <span>{btn.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <textarea
                id="messageEditor"
                value={messageTemplate}
                onChange={(e) => { if (!isReadOnly) { setMessageTemplate(e.target.value); if (error) setError(false); } }}
                placeholder="Ej: Hola {{Nombre}}, tu factura {{Factura}} vence el {{Fecha}}."
                readOnly={isReadOnly}
                className={`w-full h-[130px] max-h-[300px] overflow-y-auto resize-y rounded-md border p-3 text-sm shadow-sm focus:outline-none focus:ring-2 ${isReadOnly
                  ? "bg-gray-50 border-gray-200 text-gray-600 cursor-not-allowed"
                  : error
                    ? "border-rose-500 focus:ring-rose-500"
                    : "border-[#1CB3B3] focus:ring-[#1CB3B3]"
                  }`}
              />
            )}
            {error && <p className="text-xs text-rose-600 mt-1">Este campo es obligatorio</p>}
            {unmappedVarsError && (
              <div className="mt-2 p-3 bg-rose-50 border border-rose-200 rounded-lg">
                <p className="text-sm text-rose-700">
                  <span className="font-semibold">Datos sin conectar:</span> {unmappedVarsError}
                </p>
              </div>
            )}
            {endsWithVarError && (
              <div className="mt-2 p-4 bg-rose-50 border-2 border-rose-300 rounded-xl">
                <div className="flex items-start gap-3">
                  <span className="text-rose-500 text-lg">🚫</span>
                  <div>
                    <p className="text-sm font-semibold text-rose-800 mb-1">Plantilla no válida para Meta</p>
                    <p className="text-sm text-rose-700">{endsWithVarError}</p>
                  </div>
                </div>
              </div>
            )}
            {validationError && (
              <div className="mt-2 p-4 bg-rose-50 border-2 border-rose-300 rounded-xl">
                <div className="flex items-start gap-3">
                  <span className="text-rose-500 text-lg">🚫</span>
                  <div>
                    <p className="text-sm font-semibold text-rose-800 mb-1">Plantilla no válida para Meta</p>
                    <p className="text-sm text-rose-700">{validationError}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Editor controls - hidden in read-only mode - MOVED BEFORE variable validation */}
            {!isReadOnly && (
              <div className="mt-3 flex items-center justify-between">
                <div className="relative">
                  <Button type="button" variant="outline" size="sm" onClick={() => setShowEmojiPicker((s) => !s)} className="flex items-center gap-2">
                    <span className="text-lg">😊</span>
                    {showEmojiPicker ? 'Ocultar emojis' : 'Agregar emoji'}
                  </Button>

                  {showEmojiPicker && (
                    <div className="absolute z-50 mt-2 left-0 shadow-xl">
                      <EmojiPicker
                        onEmojiClick={insertEmoji}
                        emojiStyle={EmojiStyle.APPLE}
                        {...{ emojiData: es } as any}
                        theme={Theme.LIGHT}
                        width={320}
                        height={360}
                        searchPlaceholder="Buscar emoji..."
                        previewConfig={{ showPreview: false }}
                      />
                    </div>
                  )}
                </div>

                <div className="ml-4">
                  <Button onClick={() => { setMessageTemplate(""); }} className="bg-gradient-to-r from-[#1CB3B3] to-[#179999] text-white font-semibold px-4 py-2 rounded">Limpiar</Button>
                </div>
              </div>
            )}

            {/* New Chips Section for Variables */}
            {!isReadOnly && variables.length > 0 && (
              <div id="tour-marketing-variables-chips" className="mt-4 mb-4">
                <Label className="text-sm font-semibold text-gray-900 mb-2 block">
                  {campaignType === "Calendar" 
                    ? "Añade variables extra personalizadas al mensaje" 
                    : "Añade tus datos al mensaje: selecciona el título de la columna"}
                </Label>
                <div className="flex flex-wrap gap-2">
                  {variables.map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => insertVariable(v)}
                      className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-teal-50 text-teal-700 border border-teal-200 hover:bg-teal-100 hover:text-teal-800 transition-colors cursor-pointer"
                    >
                      {`{{${v}}}`}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {!isReadOnly && campaignType === "Calendar" && variables.length === 0 && (
              <div id="tour-marketing-variables-chips" className="mt-4 mb-4">
                <Label className="text-sm font-semibold text-gray-900 mb-2 block">
                  Añade variables extra personalizadas al mensaje
                </Label>
                <span className="text-sm text-gray-400 italic bg-gray-50 px-3 py-1 rounded-full border border-gray-100">
                  No hay variables extra
                </span>
              </div>
            )}

            {/* Real-time variable validation indicator - Only shown when creating new template */}
            {!isReadOnly && messageTemplate && (() => {
              const templateVars = extractTemplateVariables(messageTemplate);
              if (templateVars.length === 0) return null;

              const colsForIndicator = campaignType === "Calendar" ? mappableVariables : variables;
              const normalizedSheetCols = colsForIndicator.map(v => v.toLowerCase().trim());
              const ratioCheck = checkVariableToWordRatio(messageTemplate);

              const validVars = templateVars.filter(v => {
                if (/^\d+$/.test(v)) return true;
                const lower = v.toLowerCase().trim();
                if (campaignType === "Calendar" && CALENDAR_STANDARD_VAR_NAMES.has(lower)) return true;
                return normalizedSheetCols.includes(lower);
              });

              const invalidVars = templateVars.filter(v => {
                if (/^\d+$/.test(v)) return false;
                const lower = v.toLowerCase().trim();
                if (campaignType === "Calendar" && CALENDAR_STANDARD_VAR_NAMES.has(lower)) return false;
                return !normalizedSheetCols.includes(lower);
              });

              const allValid = invalidVars.length === 0;
              const isRatioOk = ratioCheck.valid;
              const wordsNeeded = !isRatioOk ? (ratioCheck.variables * 4 - ratioCheck.words) : 0;

              return (
                <div className="mt-3 space-y-2">
                  {/* Variables indicator */}
                  <div className={`p-4 rounded-xl border ${allValid ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`text-lg ${allValid ? 'text-emerald-600' : 'text-amber-600'}`}>
                        {allValid ? '✓' : '⚠️'}
                      </span>
                      <span className={`font-semibold ${allValid ? 'text-emerald-800' : 'text-amber-800'}`}>
                        {allValid
                          ? `${templateVars.length} Dato${templateVars.length > 1 ? 's' : ''} detectado${templateVars.length > 1 ? 's' : ''} correctamente`
                          : `Revisa los datos de tu mensaje`
                        }
                      </span>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {validVars.map((v, idx) => (
                        <span
                          key={`valid-${idx}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                          {`{{${v}}}`}
                        </span>
                      ))}
                      {invalidVars.map((v, idx) => (
                        <span
                          key={`invalid-${idx}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-rose-100 text-rose-700 border border-rose-200"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                          {`{{${v}}}`}
                          <span className="text-rose-400 text-[10px]">no existe</span>
                        </span>
                      ))}
                    </div>

                    {invalidVars.length > 0 && (
                      <p className="mt-3 text-xs text-amber-700">
                        <span className="font-semibold">Sugerencia:</span> Usa los botones de arriba para insertar variables que coincidan con las columnas de tu hoja de cálculo.
                      </p>
                    )}
                  </div>

                  {/* Ratio indicator */}
                  <div className={`p-4 rounded-xl border ${isRatioOk
                    ? 'bg-blue-50 border-blue-100'
                    : 'bg-amber-50 border-amber-200'}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-none ${isRatioOk ? 'bg-blue-100' : 'bg-amber-100'
                        }`}>
                        <span className="text-base">{isRatioOk ? '✓' : '⚠️'}</span>
                      </div>
                      <div>
                        <p className={`text-sm font-semibold ${isRatioOk ? 'text-blue-800' : 'text-amber-800'}`}>
                          {isRatioOk ? 'Proporción de texto válida' : 'Advertencia: Pocas palabras para tantos datos'}
                        </p>
                        <p className={`text-xs mt-0.5 ${isRatioOk ? 'text-blue-600' : 'text-amber-700'}`}>
                          {isRatioOk
                            ? `${ratioCheck.words} palabras para ${ratioCheck.variables} dato(s) - Meta aceptará esta proporción.`
                            : `Tienes ${ratioCheck.words} palabras para ${ratioCheck.variables} dato(s). Agrega ~${wordsNeeded} palabras más o Meta rechazará la plantilla.`
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* ===== SECCIÓN: ELEMENTOS ADICIONALES ===== */}
            {!isReadOnly && (
              <div id="tour-marketing-additional-elements" className="mt-6">
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Elementos adicionales</label>
                  <p className="text-[11px] text-gray-500 leading-relaxed">
                    Agrega encabezado, pie de página o botones a tu plantilla. Estos elementos son opcionales.
                  </p>
                </div>

                {/* ===== SECCIÓN: HEADER (TÍTULO) ===== */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setHeaderEnabled(!headerEnabled)}
                    className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded flex items-center justify-center ${headerEnabled ? 'bg-[#1CB3B3] text-white' : 'bg-gray-200 text-gray-500'}`}>
                        {headerType === 'IMAGE' ? <Image className="w-3.5 h-3.5" /> : <Type className="w-3.5 h-3.5" />}
                      </div>
                      <div className="text-left">
                        <div className="text-xs font-semibold text-gray-900">Encabezado (Header)</div>
                        <div className="text-[10px] text-gray-500">Agrega un título o imagen al inicio</div>
                      </div>
                    </div>
                    <div className={`w-9 h-5 rounded-full transition-colors relative ${headerEnabled ? 'bg-[#1CB3B3]' : 'bg-gray-300'}`}>
                      <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${headerEnabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </div>
                  </button>

                  {headerEnabled && (
                    <div className="px-3 py-2 border-t border-gray-200 bg-white space-y-3">
                      {/* Selector de tipo de encabezado */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">Tipo de encabezado</label>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setHeaderType('TEXT');
                              setHeaderImageHandle('');
                              setHeaderImagePreview('');
                            }}
                            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${headerType === 'TEXT'
                              ? 'border-[#1CB3B3] bg-[#E8F7F7] text-[#0D7377]'
                              : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                              }`}
                          >
                            <Type className="w-3.5 h-3.5" />
                            Texto
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setHeaderType('IMAGE');
                              setHeaderText('');
                            }}
                            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${headerType === 'IMAGE'
                              ? 'border-[#1CB3B3] bg-[#E8F7F7] text-[#0D7377]'
                              : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                              }`}
                          >
                            <Image className="w-3.5 h-3.5" />
                            Imagen
                          </button>
                        </div>
                      </div>

                      {/* Contenido según el tipo */}
                      {headerType === 'TEXT' ? (
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Texto del encabezado</label>
                          <input
                            type="text"
                            value={headerText}
                            onChange={(e) => setHeaderText(e.target.value)}
                            placeholder="Ej: ¡Hola {{Nombre}}!"
                            maxLength={60}
                            className="w-full rounded border border-[#1CB3B3] px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#1CB3B3] focus:border-[#1CB3B3]"
                          />
                          <p className="text-[10px] text-gray-400 mt-0.5">{headerText.length}/60 caracteres. Máximo 1 variable en el encabezado.</p>
                          {(() => {
                            const headerVarCount = extractTemplateVariables(headerText).length;
                            if (headerVarCount > 1) {
                              return (
                                <p className="text-[10px] text-rose-600 font-medium mt-1">
                                  El encabezado solo permite 1 variable. WhatsApp no acepta más de un dato en el header.
                                </p>
                              );
                            }
                            if (headerVarCount === 1) {
                              return (
                                <p className="text-[10px] text-emerald-600 mt-1">
                                  1 variable en el encabezado (máximo permitido).
                                </p>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      ) : (
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Imagen del encabezado</label>
                          <ImageUpload
                            onUploadComplete={(handle) => {
                              setHeaderImageHandle(handle);
                            }}
                            onPublicUrlReady={(url) => {
                              if (setHeaderImageUrl) setHeaderImageUrl(url); // ✅ Guardar la URL pública de GCS
                            }}
                            onPreviewReady={(url) => {
                              setHeaderImagePreview(url);
                            }}
                            onClear={() => {
                              setHeaderImageHandle('');
                              setHeaderImagePreview('');
                              if (setHeaderImageUrl) setHeaderImageUrl(''); // ✅ Limpiar también la URL
                            }}
                            headerHandle={headerImageHandle}
                            previewUrl={headerImagePreview}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* ===== SECCIÓN: FOOTER (PIE DE PÁGINA) ===== */}
                <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setFooterEnabled(!footerEnabled)}
                    className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded flex items-center justify-center ${footerEnabled ? 'bg-[#1CB3B3] text-white' : 'bg-gray-200 text-gray-500'}`}>
                        <FileText className="w-3.5 h-3.5" />
                      </div>
                      <div className="text-left">
                        <div className="text-xs font-semibold text-gray-900">Pie de página (Footer)</div>
                        <div className="text-[10px] text-gray-500">Texto pequeño al final del mensaje</div>
                      </div>
                    </div>
                    <div className={`w-9 h-5 rounded-full transition-colors relative ${footerEnabled ? 'bg-[#1CB3B3]' : 'bg-gray-300'}`}>
                      <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${footerEnabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </div>
                  </button>

                  {footerEnabled && (
                    <div className="px-3 py-2 border-t border-gray-200 bg-white">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Texto del pie de página</label>
                      <input
                        type="text"
                        value={footerText}
                        onChange={(e) => setFooterText(e.target.value)}
                        placeholder="Ej: Responde STOP para cancelar"
                        maxLength={60}
                        className="w-full rounded border border-[#1CB3B3] px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#1CB3B3] focus:border-[#1CB3B3]"
                      />
                      <p className="text-[10px] text-gray-400 mt-0.5">{footerText.length}/60 caracteres. No permite datos.</p>
                    </div>
                  )}
                </div>

                {/* ===== SECCIÓN: BOTONES ===== */}
                <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setButtonsEnabled(!buttonsEnabled)}
                    className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded flex items-center justify-center ${buttonsEnabled ? 'bg-[#1CB3B3] text-white' : 'bg-gray-200 text-gray-500'}`}>
                        <Plus className="w-3.5 h-3.5" />
                      </div>
                      <div className="text-left">
                        <div className="text-xs font-semibold text-gray-900">Botones</div>
                        <div className="text-[10px] text-gray-500">Agrega hasta 3 botones interactivos</div>
                      </div>
                    </div>
                    <div className={`w-9 h-5 rounded-full transition-colors relative ${buttonsEnabled ? 'bg-[#1CB3B3]' : 'bg-gray-300'}`}>
                      <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${buttonsEnabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </div>
                  </button>

                  {buttonsEnabled && (
                    <div className="px-3 py-2 space-y-2 border-t border-gray-200 bg-white">
                      {buttons.map((button, index) => (
                        <div key={index} className="p-2 border border-gray-200 rounded space-y-2 relative">
                          <button
                            type="button"
                            onClick={() => setButtons(buttons.filter((_, i) => i !== index))}
                            className="absolute top-1.5 right-1.5 p-0.5 text-gray-400 hover:text-rose-500 transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>

                          <div className="text-[10px] font-semibold text-gray-500 uppercase">Botón {index + 1}</div>

                          <div className="grid grid-cols-3 gap-1.5">
                            {[
                              { value: 'QUICK_REPLY', icon: MessageSquare, label: 'Respuesta' },
                              { value: 'URL', icon: ExternalLink, label: 'URL' },
                              { value: 'PHONE_NUMBER', icon: Phone, label: 'Llamar' },
                            ].map(({ value, icon: Icon, label }) => (
                              <button
                                key={value}
                                type="button"
                                onClick={() => {
                                  const newButtons = [...buttons];
                                  newButtons[index] = { ...newButtons[index], type: value as any };
                                  setButtons(newButtons);
                                }}
                                className={`flex items-center justify-center gap-1 py-1 px-1.5 rounded border transition-all text-[10px] ${button.type === value
                                  ? 'border-[#1CB3B3] bg-[#1CB3B3]/10 text-[#1CB3B3]'
                                  : 'border-gray-200 hover:border-gray-300 text-gray-600'
                                  }`}
                              >
                                <Icon className="w-3 h-3" />
                                <span className="font-medium">{label}</span>
                              </button>
                            ))}
                          </div>

                          <input
                            type="text"
                            value={button.text}
                            onChange={(e) => {
                              const newButtons = [...buttons];
                              newButtons[index] = { ...newButtons[index], text: e.target.value };
                              setButtons(newButtons);
                            }}
                            placeholder="Texto del botón"
                            maxLength={25}
                            className="w-full rounded border border-[#1CB3B3] px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-[#1CB3B3] focus:border-[#1CB3B3]"
                          />

                          {button.type === 'URL' && (
                            <input
                              type="url"
                              value={button.url || ''}
                              onChange={(e) => {
                                const newButtons = [...buttons];
                                newButtons[index] = { ...newButtons[index], url: e.target.value };
                                setButtons(newButtons);
                              }}
                              placeholder="https://tu-sitio.com/pagina"
                              className="w-full rounded border border-[#1CB3B3] px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-[#1CB3B3] focus:border-[#1CB3B3]"
                            />
                          )}

                          {button.type === 'PHONE_NUMBER' && (
                            <input
                              type="tel"
                              value={button.phone_number || ''}
                              onChange={(e) => {
                                const newButtons = [...buttons];
                                newButtons[index] = { ...newButtons[index], phone_number: e.target.value };
                                setButtons(newButtons);
                              }}
                              placeholder="+573001234567"
                              className="w-full rounded border border-[#1CB3B3] px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-[#1CB3B3] focus:border-[#1CB3B3]"
                            />
                          )}
                        </div>
                      ))}

                      {buttons.length < 3 && (
                        <button
                          type="button"
                          onClick={() => setButtons([...buttons, { type: 'QUICK_REPLY', text: '' }])}
                          className="w-full py-2 border border-dashed border-gray-300 rounded text-xs font-medium text-gray-600 hover:border-[#1CB3B3] hover:text-[#1CB3B3] transition-colors flex items-center justify-center gap-1.5"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Agregar botón ({buttons.length}/3)
                        </button>
                      )}

                      <p className="text-[10px] text-gray-400 leading-relaxed">
                        <strong>Respuesta:</strong> El usuario envía el texto.
                        <strong> URL:</strong> Abre enlace.
                        <strong> Llamar:</strong> Inicia llamada.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>

          <div className="flex flex-col items-center lg:sticky lg:top-36 lg:self-start lg:h-fit">
            <div className="text-lg font-semibold text-gray-900 mb-6 text-center">Vista previa</div>
            <div id="tour-marketing-template-preview" className="relative mx-auto w-full max-w-[360px] h-[600px] rounded-[3rem] bg-gray-900 border border-black/10 shadow-[0_50px_100px_-20px_rgba(0,0,0,0.5)] overflow-hidden flex flex-col ring-1 ring-black/5">
              {/* Status Bar Mock */}
              <div className="absolute top-0 w-full h-8 bg-transparent z-20 flex justify-between px-6 items-center pt-3">
                <span className="text-[12px] text-white font-medium tracking-wide">
                  {new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', hour12: true }).toLowerCase()}
                </span>
                <div className="flex gap-2 items-center">
                  <div className="w-3.5 h-3.5 border-[1.5px] border-white rounded-full"></div>
                  <div className="w-5 h-2.5 border border-white rounded-[2px] relative flex p-[1px]">
                    <div className="bg-white h-full w-[90%] rounded-[0.5px]"></div>
                    <div className="absolute -right-[2px] top-[2px] h-[3px] w-[1.5px] bg-white rounded-r-[1px]"></div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between px-4 py-3 pb-3 pt-10 flex-none bg-[#075E54] z-10 shadow-sm">
                <div className="flex items-center gap-3 text-white">
                  <div className="w-5 -ml-1"><svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"></path></svg></div>
                  <img src="https://i.pravatar.cc/120?img=32" alt="avatar" className="w-10 h-10 rounded-full object-cover border border-white/20" />
                  <div className="leading-tight">
                    <div className="text-[15px] font-semibold text-white">
                      {(() => {
                        let original = "";
                        if (sampleRow && phoneColumn && sampleRow[phoneColumn]) {
                          original = String(sampleRow[phoneColumn]);
                        }
                        // Cleaning
                        let clean = original.replace(/\D/g, "");
                        if (clean.startsWith("57")) clean = clean.substring(2);
                        // User asked for "following 11 numbers". Usually it's 10 for mobile.
                        // We'll just take the first 10-11 chars of the remainder.
                        if (clean.length > 0) clean = clean.substring(0, 11);

                        return clean ? `+57 ${clean}` : "+57 ··· ··· ····";
                      })()}
                    </div>
                    <div className="text-[11px] text-white/90">en línea</div>
                  </div>
                </div>
              </div>
              <div className="bg-[#efeae2] p-3 flex-1 overflow-hidden flex flex-col relative" style={{ minHeight: 0 }}>
                <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: "url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')", backgroundSize: "400px", opacity: 0.4 }} />

                <div className="flex-1 flex flex-col overflow-y-auto p-3" style={{ maxHeight: '380px' }}>
                  <div className="flex justify-end mt-auto">
                    <div className="max-w-[84%] relative z-10">
                      {/* Message Bubble Container - grows naturally to fit content */}
                      <div className="bg-[#D9FDD3] rounded-lg shadow-sm overflow-hidden" style={{ borderTopLeftRadius: '8px', borderTopRightRadius: '0px', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}>

                        {/* HEADER Preview - Para plantilla nueva O plantilla existente */}
                        {(() => {
                          // ✅ Mostrar imagen si headerType es IMAGE y hay preview (nueva plantilla)
                          const showNewImageHeader = !isReadOnly && headerEnabled && headerType === 'IMAGE' && headerImagePreview;
                          const showNewTextHeader = !isReadOnly && headerEnabled && headerType === 'TEXT' && headerText.trim();

                          // ✅ Para plantillas existentes: verificar si tiene imagen o texto
                          const showExistingImageHeader = isReadOnly && (selectedTemplate?.header_type === 'IMAGE' || selectedTemplate?.header_image_url) && selectedTemplate?.header_image_url;
                          const showExistingTextHeader = isReadOnly && existingTemplateHeader;

                          if (showNewImageHeader) {
                            return (
                              <div className="w-full">
                                <img
                                  src={headerImagePreview}
                                  alt="Header"
                                  className="w-full h-auto object-cover max-h-[150px]"
                                />
                              </div>
                            );
                          }

                          if (showNewTextHeader) {
                            return (
                              <div className="px-4 pt-3 pb-1">
                                <p className="font-bold text-sm text-gray-900 break-words">{headerText}</p>
                              </div>
                            );
                          }

                          // ✅ Mostrar imagen de plantilla existente
                          if (showExistingImageHeader) {
                            return (
                              <div className="w-full">
                                <img
                                  src={selectedTemplate.header_image_url}
                                  alt="Header"
                                  className="w-full h-auto object-cover max-h-[150px]"
                                />
                              </div>
                            );
                          }

                          // ✅ Mostrar texto de plantilla existente
                          if (showExistingTextHeader) {
                            const headerPreview = buildWhatsAppPreviewText(
                              existingTemplateHeader,
                              headerNumericVariables,
                            );
                            return (
                              <div className="px-4 pt-3 pb-1">
                                <p className="font-bold text-sm text-gray-900 break-words">{headerPreview}</p>
                              </div>
                            );
                          }

                          return null;
                        })()}

                        {/* BODY */}
                        <div className="px-4 py-2">
                          <div className="text-sm text-black leading-relaxed break-words whitespace-pre-wrap [&_img]:inline [&_img]:w-5 [&_img]:h-5 [&_img]:align-text-bottom [&_img]:mx-[1px]">
                            {effectiveMessageTemplate ? (
                              <Twemoji
                                text={buildWhatsAppPreviewText(
                                  effectiveMessageTemplate,
                                  bodyNumericVariables,
                                )}
                                svg
                              />
                            ) : (
                              <p className="text-gray-500">Tu mensaje aparecerá aquí...</p>
                            )}
                          </div>
                        </div>

                        {/* FOOTER Preview - Para plantilla nueva O plantilla existente */}
                        {(() => {
                          const showNewFooter = !isReadOnly && footerEnabled && footerText.trim();
                          const showExistingFooter = isReadOnly && existingTemplateFooter;

                          if (showNewFooter) {
                            return (
                              <div className="px-4 pb-2">
                                <p className="text-xs text-gray-500 break-words">{footerText}</p>
                              </div>
                            );
                          }

                          if (showExistingFooter) {
                            return (
                              <div className="px-4 pb-2">
                                <p className="text-xs text-gray-500 break-words">{existingTemplateFooter}</p>
                              </div>
                            );
                          }

                          return null;
                        })()}

                        {/* Timestamp */}
                        <div className="text-[10px] text-gray-500 px-4 pb-2 text-right">
                          {new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', hour12: false })}
                          <span className="ml-1 text-[#34B7F1]">✓✓</span>
                        </div>
                      </div>

                      {/* BUTTONS Preview - Para plantilla nueva O plantilla existente */}
                      {(() => {
                        const showNewButtons = !isReadOnly && buttonsEnabled && buttons.length > 0;
                        const showExistingButtons = isReadOnly && existingTemplateButtons.length > 0;

                        if (showNewButtons) {
                          return (
                            <div className="mt-[2px] overflow-hidden rounded-lg bg-white/95 shadow-sm divide-y divide-gray-200">
                              {buttons.filter(b => b.text.trim()).map((button, idx) => (
                                <div
                                  key={idx}
                                  className="py-2.5 px-4 text-center"
                                >
                                  <span className="text-sm font-medium text-[#00A884] flex items-center justify-center gap-2">
                                    {button.type === 'URL' && <ExternalLink className="w-4 h-4" />}
                                    {button.type === 'PHONE_NUMBER' && <Phone className="w-4 h-4" />}
                                    {button.type === 'QUICK_REPLY' && <Reply className="w-4 h-4" />}
                                    {button.text}
                                  </span>
                                </div>
                              ))}
                            </div>
                          );
                        }

                        if (showExistingButtons) {
                          return (
                            <div className="mt-[2px] overflow-hidden rounded-lg bg-white/95 shadow-sm divide-y divide-gray-200">
                              {existingTemplateButtons.map((button: any, idx: number) => {
                                const btnText = buildWhatsAppPreviewText(
                                  button.text || "",
                                  extractNumericVariables(button.text || ""),
                                );

                                return (
                                  <div
                                    key={idx}
                                    className="py-2.5 px-4 text-center"
                                  >
                                    <span className="text-sm font-medium text-[#00A884] flex items-center justify-center gap-2">
                                      {button.type === 'URL' && <ExternalLink className="w-4 h-4" />}
                                      {button.type === 'PHONE_NUMBER' && <Phone className="w-4 h-4" />}
                                      {button.type === 'QUICK_REPLY' && <Reply className="w-4 h-4" />}
                                      {btnText}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        }

                        return null;
                      })()}
                    </div>
                  </div>
                </div>

                <div className="mt-3 bg-white rounded-full px-3 py-2 flex items-center gap-3 shadow-sm flex-none">
                  <div className="w-9 h-9 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-green-600 text-xl font-bold">+</div>
                  <div className="flex items-center gap-2 flex-1">
                    <div className="flex-1 text-base text-gray-500 select-none">Escribe un mensaje...</div>
                  </div>
                  <div className="w-9 h-9 rounded-full bg-[#25D366] flex items-center justify-center text-white text-sm">✓</div>
                </div>
              </div>
            </div>

            {/* Explanatory text below phone preview */}
            <div className="mt-4 text-center max-w-[360px]">
              <p className="text-sm text-slate-500 leading-relaxed">
                {campaignType === "Calendar"
                  ? sampleRow
                    ? "Vista previa con datos del evento más reciente de tu calendario. Solo se reemplazan las variables que hayas mapeado."
                    : "Carga eventos en tu calendario para ver la vista previa con datos reales. Las variables aparecen como {{1}}, {{Fecha}}, etc. hasta que las mapees."
                  : "Este es un ejemplo de cómo verán el mensaje tus clientes. Se utiliza la primera fila de tu hoja de cálculo como datos de prueba."
                }
              </p>
            </div>
          </div>
        </div>
      </CardContent>

      {!hideNavigation && (
        <CardFooter className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 bg-gray-50/80 px-4 sm:px-8 py-5 border-t border-gray-100 mt-2 rounded-b-xl">
          <Button
            variant="outline"
            onClick={onBack}
            className="w-full sm:w-auto border-gray-300 text-gray-600 hover:bg-gray-50 px-6 h-11 rounded-xl shadow-sm bg-white"
          >
            ← Atrás
          </Button>
          <Button
            onClick={handleNext}
            className="w-full sm:w-auto bg-gradient-to-r from-[#1CB3B3] to-[#179999] hover:from-[#179999] hover:to-[#1CB3B3] text-white font-semibold px-8 h-11 rounded-xl shadow-lg hover:shadow-cyan-500/30 transform transition-all active:scale-95"
          >
            Siguiente →
          </Button>
        </CardFooter>
      )}
    </Card >
  );
};



export default StepTemplateMessage;
